/**
 * Clientes (DEMO-03, telas/Clientes.tsx), agora persistente: a lista mostra o
 * GRUPO de Cliente (sem uma linha por filial). O Cliente 360° mostra a Matriz,
 * as Filiais (entidades legais com CNPJ próprio) e os contatos de cada uma.
 * Faturamento e entrega NÃO ficam no cadastro: são definidos na Proposta.
 * Timeline e Oportunidades chegaram com a B2; as abas que dependem de módulos
 * ainda não portados aparecem "em breve", como o Financeiro na DEMO-03; Documentos chegou na Fase 1 (C18).
 */
import { PedidosDoCliente, type PedidoResumo } from './Pedidos.tsx';
import { cabecalhoCliente, valorReferencia } from '../../../backend/src/compartilhado/indicadores.ts';
import { Fragment, useState, type ReactNode } from 'react';
import { useRotuloTrilha } from '../componentes/trilha.tsx';

import { api, consulta } from '../api.ts';
import { useSessao } from '../sessao.tsx';
import { useAmbiente } from '../ambiente.tsx';
import { validarCep } from '../../../backend/src/compartilhado/documentos.ts';
import { Abas, Ajuda, CampoBusca, EstadoCarga, Modal, ResumoIndicadores, SeloSituacao, Th, useCarga, useOrdenacao } from './ui.tsx';
import { BarraFiltros, FiltroPeriodo, SeloTipo, filtroSelecao, filtroVarios, useSituacoes } from './filtros.tsx';
import { LinhaPersonalizacao, usePersonalizacao } from './personalizacao.tsx';
import { Icone } from '../componentes/icones.tsx';
import { SeloOrdem, tomOrdem, type DocResumo, type OcfResumo } from './Ordens.tsx';
import { ESTADOS_ORDEM } from '../../../backend/src/compartilhado/ordens.ts';
import { SITUACOES_VERSAO, type SituacaoVersao } from '../../../backend/src/compartilhado/propostas.ts';
import { moeda, pct } from './tiposOportunidade.ts';
import { AJUDA_CEP_CONSULTA, AJUDA_CNPJ_CONSULTA, CampoCep, CampoCnpj, CampoOrigem, SeletorUf, useConsultaCep, useConsultaCnpj, type DadosCnpjTela } from './campos.tsx';
import { ModalContato } from './Contatos.tsx';
import { DocumentosDoCliente } from './DocumentosCliente.tsx';
import { TimelineCliente } from './Atividades.tsx';
import { LinhaTotais, ListaOportunidades, useDadosOportunidades, useFiltrosOportunidades } from './Oportunidades.tsx';
import type { OportunidadeResumo } from './tiposOportunidade.ts';
import { ModalNovaOportunidade } from './NovaOportunidade.tsx';
import { navegar } from '../rotas.ts';
import { PRESETS, TIPOS, dataBr, estagioAberto, intervalo, periodoInicial, type Estagio, type Periodo, type Preset, type Tipo } from '../../../backend/src/compartilhado/oportunidades.ts';
import type { ComportamentoComercial as ComportamentoComercialDados } from '../../../backend/src/compartilhado/marketing.ts';
import { hojeIso } from './CampoData.tsx';
import {
  CAMPOS_PERFIL,
  ROTULO_IE,
  ROTULO_SITUACAO_CADASTRAL,
  ROTULO_TIPO_ENTIDADE,
  ROTULOS_PERFIL,
  enderecoTexto,
  indicacaoDe,
  matrizDe,
  rotuloEntidade,
  rotuloStatus,
  TIPOS_ENTIDADE,
  textoIndicacao,
  type Cliente,
  type ClienteResumo,
  type Contato,
  type Entidade,
  type Indicacao,
  type Opcoes,
  type SituacaoCadastral,
  type SituacaoIe,
} from './tipos.ts';

type ColCliente = 'codigo' | 'nome' | 'cnpj' | 'filiais' | 'segmento' | 'cidade' | 'responsavel' | 'status';
/** Valores distintos de um texto livre (Segmento/Subsegmento não têm lista oficial — ver C4), em ordem alfabética. */
const distintos = (xs: (string | null | undefined)[]) => [...new Set(xs.map((x) => (x ?? '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pt-BR'));
const opcoesDe = (xs: string[]) => xs.map((x) => ({ id: x, nome: x }));

export function ListaClientes() {
  const { pode } = useSessao();
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState<'' | 'ativo' | 'inativo'>('');
  const [segmento, setSegmento] = useState('');
  const [subsegmento, setSubsegmento] = useState('');
  const [ufs, setUfs] = useState<string[]>([]);
  const [responsavel, setResponsavel] = useState('');
  const [cidade, setCidade] = useState('');
  const ord = useOrdenacao<ColCliente>('nome');
  // P2-03/P2-04 (D7)
  const pers = usePersonalizacao('clientes', { busca: { valor: busca, definir: setBusca },
    filtros: { status, segmento, subsegmento, ufs, responsavel, cidade },
    aplicarFiltros: (e: Record<string, unknown>) => (setStatus((e.status as '' | 'ativo' | 'inativo') ?? ''), setSegmento((e.segmento as string) ?? ''), setSubsegmento((e.subsegmento as string) ?? ''), setUfs((e.ufs as string[]) ?? []), setResponsavel((e.responsavel as string) ?? ''), setCidade((e.cidade as string) ?? '')),
    ordenacao: ord,
  });
  const carga = useCarga(() => api.get<ClienteResumo[]>(`/api/clientes${consulta({ busca: busca.trim() })}`), [busca]);
  const todos = carga.dados ?? [];
  // P2-07: opções a partir dos próprios Clientes; Subsegmento depende do Segmento escolhido
  const segmentos = distintos(todos.map((c) => c.segmento));
  const subsegmentos = distintos(todos.filter((c) => !segmento || (c.segmento ?? '').trim() === segmento).map((c) => c.subsegmento));
  const ufsDisponiveis = distintos(todos.map((c) => c.matriz?.uf));
  const responsaveis = distintos(todos.map((c) => c.responsavel_nome));
  const cidades = distintos(todos.filter((c) => !ufs.length || ufs.includes(c.matriz?.uf ?? '')).map((c) => c.matriz?.cidade));
  const filtrados = todos.filter(
    (c) =>
      (!status || c.status === status) &&
      (!segmento || (c.segmento ?? '').trim() === segmento) &&
      (!subsegmento || (c.subsegmento ?? '').trim() === subsegmento) &&
      (!ufs.length || ufs.includes(c.matriz?.uf ?? '')) &&
      (!responsavel || c.responsavel_nome === responsavel) &&
      (!cidade || (c.matriz?.cidade ?? '').trim() === cidade),
  );
  const lista = ord.ordenar(filtrados, {
    codigo: (c) => c.codigo,
    nome: (c) => c.nome,
    cnpj: (c) => c.matriz?.cnpj,
    filiais: (c) => c.filiais,
    segmento: (c) => [c.segmento, c.subsegmento].filter(Boolean).join(' · '),
    cidade: (c) => (c.matriz ? `${c.matriz.cidade}/${c.matriz.uf}` : null),
    responsavel: (c) => c.responsavel_nome,
    status: (c) => c.status,
  });
  const comFiltro = Boolean(busca.trim() || status || segmento || subsegmento || ufs.length || responsavel || cidade);
  const limpar = () => (setBusca(''), setStatus(''), setSegmento(''), setSubsegmento(''), setUfs([]), setResponsavel(''), setCidade(''));
  return (
    <>
      {/* P2-07: como nas demais listas, a trilha é o título; linha do topo = contagem · ajuda · Novo cliente */}
      <div className="titulo-pag-linha">
        <span className="linha-totais" data-teste="resumo-clientes">
          <b>{carga.dados ? lista.length : '…'}</b> cliente(s)
        </span>
        <Ajuda titulo="Clientes">Um registro por Grupo de Cliente; filiais e demais entidades ficam no detalhe do Cliente.</Ajuda>
        <span className="espaco" />
        {pode('criar', 'cliente') && (
          <a className="botao-primario" href="#/clientes/novo" data-teste="novo-cliente">
            Novo cliente
          </a>
        )}
      </div>
      <BarraFiltros
        teste="clientes"
        direita={pers.controles}
        fixos={
          <>
            <CampoBusca valor={busca} setValor={setBusca} rotulo="Buscar cliente" ajuda="Nome, código, CNPJ ou contato" dica="Nome, código, razão social, CNPJ (com ou sem máscara), contato, e-mail ou telefone" teste="busca-clientes" total={busca.trim() ? lista.length : null} />
            <label>
              Status
              <select value={status} onChange={(e) => setStatus(e.target.value as '' | 'ativo' | 'inativo')} data-teste="filtro-status-cliente">
                <option value="">Todos</option>
                <option value="ativo">Ativos</option>
                <option value="inativo">Inativos</option>
              </select>
            </label>
            <label>
              Segmento
              <select value={segmento} onChange={(e) => (setSegmento(e.target.value), setSubsegmento(''))} data-teste="filtro-segmento-cliente">
                <option value="">Todos</option>
                {segmentos.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
          </>
        }
        opcionais={[
          filtroSelecao('subsegmento', 'Subsegmento', subsegmento, setSubsegmento, opcoesDe(subsegmentos), 'filtro-subsegmento-cliente'),
          filtroVarios('uf', 'UF', ufs, (v) => (setUfs(v), setCidade('')), ufsDisponiveis, 'filtro-uf-cliente'),
          filtroSelecao('responsavel', 'Responsável', responsavel, setResponsavel, opcoesDe(responsaveis), 'filtro-responsavel-cliente'),
          filtroSelecao('cidade', 'Cidade', cidade, setCidade, opcoesDe(cidades), 'filtro-cidade-cliente', 'Todas'),
        ]}
        aoLimpar={limpar}
      />
      <EstadoCarga carregando={carga.carregando && !carga.dados} erro={carga.erro} recarregar={carga.recarregar} teste="clientes" />
      {carga.dados && lista.length === 0 && (
        <p className="vazio" data-teste="clientes-vazio">
          {busca.trim() && !comFiltro ? `Nenhum cliente com “${busca}”. Confira o CNPJ, o nome ou o contato.` : comFiltro ? (busca.trim() ? `Nenhum cliente com “${busca}” e os filtros atuais.` : 'Nenhum cliente com os filtros atuais.') : 'Nenhum cliente cadastrado ainda.'}
        </p>
      )}
      {lista.length > 0 && (
        <div className="rolagem quadro-tabela primeira-fixa">
          <table ref={pers.tabela} className="tabela" data-teste="lista-clientes">
            <thead>
              <tr>
                <Th ord={ord} coluna="codigo">Código</Th>
                <Th ord={ord} coluna="nome" className="col-nome-cliente">Grupo de Cliente</Th>
                <Th ord={ord} coluna="cnpj">CNPJ principal</Th>
                <Th ord={ord} coluna="filiais" padrao="desc" numero>Filiais</Th>
                <Th ord={ord} coluna="segmento">Segmento</Th>
                <Th ord={ord} coluna="cidade">Cidade/UF (principal)</Th>
                <Th ord={ord} coluna="responsavel">Responsável</Th>
                <Th ord={ord} coluna="status">Status</Th>
              </tr>
            </thead>
            <tbody>
              {lista.map((c) => (
                <tr key={c.id}>
                  <td>{c.codigo}</td>
                  <td className="col-nome-cliente">
                    {/* Revisão 12/10 (02d): nome em até 2 linhas (texto completo no title); razão social só quando difere do nome */}
                    <a href={`#/clientes/${c.id}`} className="duas-linhas" title={c.nome} data-teste={`nome-cliente-${c.codigo}`}>
                      {c.nome}
                    </a>
                    {c.matriz?.razao_social && normalizarNome(c.matriz.razao_social) !== normalizarNome(c.nome) && (
                      <div className="texto-secundario duas-linhas" title={c.matriz.razao_social} data-teste={`razao-cliente-${c.codigo}`}>
                        {c.matriz.razao_social}
                      </div>
                    )}
                    {c.encontrado_por && c.encontrado_por.tipo !== 'cliente' && (
                      <div className="texto-secundario" data-teste={`encontrado-por-${c.codigo}`}>
                        {c.encontrado_por.tipo === 'legado' ? `Encontrado pelo código legado ${c.encontrado_por.codigo}${c.encontrado_por.entidade ? ` → ${c.encontrado_por.entidade}` : ''}` : `Encontrado pela entidade ${c.encontrado_por.codigo}`}
                      </div>
                    )}
                  </td>
                  <td className="nowrap">{c.matriz?.cnpj}</td>
                  <td className="numero">{c.filiais}</td>
                  <td>{[c.segmento, c.subsegmento].filter(Boolean).join(' · ') || '—'}</td>
                  <td>{c.matriz ? `${c.matriz.cidade}/${c.matriz.uf}` : '—'}</td>
                  <td>{c.responsavel_nome}</td>
                  <td>{rotuloStatus(c.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="rodape-tabela">{lista.length} cliente(s)</div>
        </div>
      )}
    </>
  );
}

/** Compara nome e razão social sem diferenças de caixa, acentos, pontuação e espaços (ex.: "S.A." × "SA"). */
const normalizarNome = (x: string) =>
  x
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/gi, '')
    .toLowerCase();

type AbaCliente = 'entidades' | 'contatos' | 'oportunidades' | 'propostas' | 'pedidos' | 'ordens' | 'financeiro' | 'marketing' | 'documentos' | 'historico';

export function Cliente360({ id }: { id: string }) {
  const { pode } = useSessao();
  const { setSituacoes } = useSituacoes();
  const [aba, setAba] = useState<AbaCliente>('entidades');
  const [editarContato, setEditarContato] = useState<Contato | null>(null);
  const [novoContato, setNovoContato] = useState(false);
  const [entidade, setEntidade] = useState<Entidade | 'nova' | null>(null);
  const [editarCliente, setEditarCliente] = useState(false);
  const [mudarStatus, setMudarStatus] = useState(false);
  const [principal, setPrincipal] = useState<Entidade | null>(null);
  const [transferir, setTransferir] = useState<Entidade | null>(null);
  // filtro "Entidade" nas abas de negócios e contatos: '' = consolidado do Cliente (padrão)
  const [entidadeFiltro, setEntidadeFiltro] = useState('');
  const carga = useCarga(() => api.get<Cliente>(`/api/clientes/${id}`), [id]);
  // revisão 12/10: contagem de documentos ativos na aba Documentos
  const verDocumentos = pode('visualizar', 'documento_cliente');
  const [versaoDocs, setVersaoDocs] = useState(0);
  const docs = useCarga(() => (verDocumentos ? api.get<{ documentos: { situacao: string }[] }>(`/api/clientes/${id}/documentos`) : Promise.resolve(null)), [id, verDocumentos, versaoDocs]);
  const qtdDocumentos = docs.dados?.documentos.filter((d) => d.situacao === 'ativo').length;
  // P2-20/P2-21 (D2): valores do cabeçalho = cliente inteiro, histórico completo, sem os filtros das abas
  const verOp = pode('visualizar', 'oportunidade');
  const verPed = pode('visualizar', 'pedido');
  const valores = useCarga(async () => {
    const [ops, peds] = await Promise.all([verOp ? api.get<OportunidadeResumo[]>(`/api/oportunidades${consulta({ cliente_id: id })}`) : Promise.resolve(null), verPed ? api.get<PedidoResumo[]>(`/api/pedidos${consulta({ cliente_id: id })}`) : Promise.resolve(null)]);
    // Lixeira: ganhas cujo Pedido está na Lixeira (contam em "Ganhas", não em "Pedidos gerados")
    return { ...cabecalhoCliente(ops ?? [], peds ?? []), pedidosNaLixeira: (ops ?? []).filter((o) => o.pedido_na_lixeira).map((o) => o.pedido_na_lixeira as string) };
  }, [id, verOp, verPed]);
  useRotuloTrilha(carga.dados?.nome);
  const c = carga.dados;
  if (!c) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="cliente" />;
  const matriz = matrizDe(c);
  const v = valores.dados;
  // P2-18/P2-22/P2-23: "Filial/unidade" (antes "Entidade" fora das abas) — só quando o Cliente tem mais de uma entidade
  const seletorFilial =
    c.entidades.length > 1 ? (
      <label className="filtro-entidade-cliente">
        Filial/unidade
        <select value={entidadeFiltro} onChange={(e) => setEntidadeFiltro(e.target.value)} data-teste="filtro-entidade-cliente">
          <option value="">Todas (matriz e filiais)</option>
          {c.entidades.map((e) => (
            <option key={e.id} value={e.id}>
              {rotuloEntidade(e)}
            </option>
          ))}
        </select>
      </label>
    ) : null;
  const ativo = c.status === 'ativo';
  const podeEditar = pode('editar', 'cliente') && ativo;
  const recarregar = () => void carga.recarregar();
  const indicacao = indicacaoDe(c);
  return (
    <>
      {/* F1-13/F1-14/F1-15: cabeçalho comum a todas as abas — identificação compacta + 5 indicadores (oportunidades distintas) */}
      <section className="resumo-destaque resumo-cliente cabecalho-cliente" data-teste="resumo-cliente" aria-label="Resumo do Cliente">
        <div className="linha-identificacao">
          <span className="avatar-registro" aria-hidden="true">
            <Icone nome="clientes" />
          </span>
          <div className="identificacao">
            <h1 className="nome-registro" title={c.nome}>
              {c.nome}
            </h1>
            <div className="ident-linha">
              <span className="nowrap" data-teste="codigo-cliente">
                {c.codigo}
              </span>
              <SeloSituacao texto={rotuloStatus(c.status)} tom={ativo ? 'positivo' : 'encerrado'} />
              <span className="ponto">·</span>
              <span>Responsável: {c.responsavel_nome ?? '—'}</span>
              <span className="ponto">·</span>
              <span data-teste="entidade-principal">
                {c.entidades.length} entidade(s) legal(is) ({c.entidades.filter((e) => e.tipo === 'filial').length} filial(is)) · principal <span className="nowrap">{matriz.codigo ?? matriz.cnpj}</span>
                {matriz.principal_provisoria && <span className="selo selo-alerta" title="Sem estabelecimento /0001 na base: principal provisória até a Matriz ser identificada"> provisória</span>}
              </span>
            </div>
          </div>
          <div className="acoes-direita">
            {podeEditar && (
              <button type="button" className="botao-secundario" onClick={() => setEditarCliente(true)} data-teste="editar-cliente">
                Editar cliente
              </button>
            )}
            {pode('inativar', 'cliente') && (
              <button type="button" className="botao-secundario" onClick={() => setMudarStatus(true)} data-teste={ativo ? 'inativar-cliente' : 'reativar-cliente'}>
                {ativo ? 'Inativar' : 'Reativar'}
              </button>
            )}
          </div>
        </div>
        {/* P2-20/P2-21 (D2): quantidade + R$; Ticket médio; Última interação preservada */}
        <ResumoIndicadores
          teste="indicadores-cliente"
          itens={[
            { rotulo: 'Total de oportunidades', valor: c.resumo?.oportunidades ?? 0, detalhe: v && verOp ? `${moeda(v.total.valorAbertasGanhas)} em abertas + ganhas` : undefined, icone: 'grupo', teste: 'kpi-cliente-total' },
            { rotulo: 'Em aberto', valor: c.resumo?.oportunidades_abertas ?? 0, detalhe: v && verOp ? moeda(v.abertas.valor) : undefined, icone: 'ciclo', teste: 'kpi-cliente-abertas' },
            { rotulo: 'Ganhas', valor: c.resumo?.oportunidades_ganhas ?? 0, detalhe: v && verOp ? moeda(v.ganhas.valor) : undefined, icone: 'trofeu', teste: 'kpi-cliente-ganhas' },
            { rotulo: 'Pedidos gerados', valor: v && verPed ? v.pedidos.quantidade : (c.resumo?.pedidos ?? 0), detalhe: v && verPed ? moeda(v.pedidos.valor) : undefined, icone: 'documento', teste: 'kpi-cliente-pedidos', ajuda: v?.pedidosNaLixeira.length ?<Ajuda titulo="Pedidos na Lixeira">{`${v.pedidosNaLixeira.join(', ')} ${v.pedidosNaLixeira.length === 1 ? 'está' : 'estão'} na Lixeira: fora de "Pedidos gerados" e do ticket médio. ${v.pedidosNaLixeira.length === 1 ? 'A Oportunidade continua' : 'As Oportunidades continuam'} contando em "Ganhas".`}</Ajuda> : undefined },
            { rotulo: 'Ticket médio', valor: v && verPed ? (v.ticketMedio === null ? '—' : moeda(v.ticketMedio)) : '—', detalhe: v && verPed ? `${v.pedidos.quantidade} ${v.pedidos.quantidade === 1 ? 'pedido' : 'pedidos'}` : undefined, icone: 'etiqueta', teste: 'kpi-cliente-ticket' },
            {
              rotulo: 'Última interação',
              valor: c.resumo?.ultima_interacao ? dataBr(new Date(c.resumo.ultima_interacao).toLocaleDateString('sv-SE', { timeZone: 'America/Sao_Paulo' })) : '—',
              detalhe: [c.resumo?.ultima_interacao_tipo, c.resumo?.ultima_interacao_autor].filter(Boolean).join(' · ') || undefined,
              icone: 'relogio',
            },
          ]}
        />
      </section>
      {/* F1-17: ordem das abas aprovada; rolagem horizontal em telas estreitas (nenhuma aba some) */}
      <div className="abas-rolagem">
        <Abas<AbaCliente>
          rotulo="Seções do cliente"
          atual={aba}
          aoTrocar={setAba}
          abas={[
            { id: 'entidades', rotulo: `Entidades legais (${c.entidades.length})` },
            { id: 'contatos', rotulo: `Contatos (${c.contatos.length})` },
            { id: 'oportunidades', rotulo: `Oportunidades (${c.resumo?.oportunidades ?? 0})`, desabilitada: pode('visualizar', 'oportunidade') ? undefined : 'Sem permissão para ver oportunidades' },
            { id: 'propostas', rotulo: `Propostas (${c.resumo?.propostas ?? 0})`, desabilitada: pode('visualizar', 'proposta') && pode('visualizar', 'oportunidade') ? undefined : 'Sem permissão para ver propostas' },
            { id: 'pedidos', rotulo: `Pedidos (${c.resumo?.pedidos ?? 0})`, desabilitada: pode('visualizar', 'pedido') ? undefined : 'Sem permissão para ver pedidos' },
            { id: 'ordens', rotulo: `Ordens de compra (${c.resumo?.ordens ?? 0})`, desabilitada: pode('visualizar', 'ordem') ? undefined : 'Sem permissão para ver ordens' },
            { id: 'financeiro', rotulo: 'Financeiro', desabilitada: 'Planejado para o MVP 2' },
            { id: 'marketing', rotulo: 'Marketing' },
            { id: 'documentos', rotulo: qtdDocumentos === undefined ? 'Documentos' : `Documentos (${qtdDocumentos})`, desabilitada: verDocumentos ? undefined : 'Sem permissão para ver documentos' },
            { id: 'historico', rotulo: 'Histórico', desabilitada: pode('visualizar', 'atividade') ? undefined : 'Sem permissão para ver atividades' },
          ]}
        />
      </div>
      {aba === 'entidades' && (
        <EntidadesDoCliente
          cliente={c}
          podeEditar={podeEditar}
          podeTransferir={pode('transferir_entidade', 'cliente') && ativo}
          aoNova={() => setEntidade('nova')}
          aoEditar={setEntidade}
          aoPrincipal={setPrincipal}
          aoTransferir={setTransferir}
        />
      )}
      {aba === 'marketing' && <MarketingDoCliente cliente={c} aoVerPerdas={() => (setSituacoes(['perdida']), setAba('oportunidades'))} />}
      {aba === 'documentos' && <DocumentosDoCliente clienteId={c.id} clienteAtivo={ativo} aoMudar={() => setVersaoDocs((x) => x + 1)} />}
      {(aba === 'propostas' || aba === 'contatos') && seletorFilial && <div className="filial-compacta">{seletorFilial}</div>}
      {aba === 'historico' && <TimelineCliente clienteId={c.id} aoMudar={recarregar} />}
      {aba === 'pedidos' && (
        <section data-teste="aba-pedidos-cliente">
          <PedidosDoCliente key={entidadeFiltro} clienteId={c.id} entidadeId={entidadeFiltro || undefined} filial={seletorFilial} aoLimparFilial={() => setEntidadeFiltro('')} />
        </section>
      )}
      {aba === 'propostas' && <PropostasDoCliente key={entidadeFiltro} cliente={c} entidadeId={entidadeFiltro || undefined} />}
      {aba === 'ordens' && <OrdensDoCliente cliente={c} />}
      {aba === 'oportunidades' && <OportunidadesDoCliente key={entidadeFiltro} cliente={c} entidadeId={entidadeFiltro || undefined} podeCriar={pode('criar', 'oportunidade') && ativo} filial={seletorFilial} aoLimparFilial={() => setEntidadeFiltro('')} />}
      {aba === 'contatos' && <ContatosDoCliente cliente={entidadeFiltro ? { ...c, contatos: c.contatos.filter((k) => k.entidade_id === entidadeFiltro) } : c} aoEditar={setEditarContato} aoNovo={pode('criar', 'contato') && ativo ? () => setNovoContato(true) : undefined} podeEditar={pode('editar', 'contato')} />}
      {editarContato && <ModalContato contato={editarContato} clienteId={c.id} entidades={c.entidades} aoFechar={() => setEditarContato(null)} aoSalvar={() => (setEditarContato(null), recarregar())} />}
      {novoContato && <ModalContato clienteId={c.id} entidades={c.entidades} aoFechar={() => setNovoContato(false)} aoSalvar={() => (setNovoContato(false), recarregar())} />}
      {entidade && <ModalEntidadeCliente cliente={c} entidade={entidade === 'nova' ? undefined : entidade} aoFechar={() => setEntidade(null)} aoSalvar={() => (setEntidade(null), recarregar())} />}
      {principal && <ModalDefinirPrincipal cliente={c} entidade={principal} aoFechar={() => setPrincipal(null)} aoSalvar={() => (setPrincipal(null), recarregar())} />}
      {transferir && <ModalTransferirEntidade cliente={c} entidade={transferir} aoFechar={() => setTransferir(null)} />}
      {editarCliente && <ModalEditarCliente cliente={c} aoFechar={() => setEditarCliente(false)} aoSalvar={() => (setEditarCliente(false), recarregar())} />}
      {mudarStatus && <ModalStatusCliente cliente={c} aoFechar={() => setMudarStatus(false)} aoSalvar={() => (setMudarStatus(false), recarregar())} />}
    </>
  );
}

type ColEnt = 'codigo' | 'tipo' | 'razao' | 'cidade' | 'situacao' | 'status';
/**
 * Aba Entidades legais (F1-18): tabela compacta — Código e Tipo em colunas próprias, cada uma ordenável;
 * Situação cadastral (Receita) × Status no HUB; linha expansível com nome fantasia, endereço, IE e contatos;
 * ações por permissão. Filtro de Status no HUB (padrão: ativas) e de Tipo; rodapé só com a contagem.
 */
function EntidadesDoCliente({ cliente: c, podeEditar, podeTransferir, aoNova, aoEditar, aoPrincipal, aoTransferir }: { cliente: Cliente; podeEditar: boolean; podeTransferir: boolean; aoNova: () => void; aoEditar: (e: Entidade) => void; aoPrincipal: (e: Entidade) => void; aoTransferir: (e: Entidade) => void }) {
  const [status, setStatus] = useState<'ativo' | 'inativo' | ''>('ativo');
  const [tipo, setTipo] = useState('');
  const [aberta, setAberta] = useState<string | null>(null);
  const ord = useOrdenacao<ColEnt>('codigo');
  const pers = usePersonalizacao('cliente-entidades', { filtros: { status, tipo }, aplicarFiltros: (e: Record<string, unknown>) => (setStatus((e.status as 'ativo' | 'inativo' | '') ?? 'ativo'), setTipo((e.tipo as string) ?? '')), ordenacao: ord });
  const filtradas = c.entidades.filter((e) => (!status || e.status === status) && (!tipo || e.tipo === tipo));
  const lista = ord.ordenar(
    filtradas,
    { codigo: (e) => e.codigo ?? e.cnpj, tipo: (e) => ROTULO_TIPO_ENTIDADE[e.tipo], razao: (e) => e.razao_social, cidade: (e) => `${e.cidade}/${e.uf}`, situacao: (e) => ROTULO_SITUACAO_CADASTRAL[e.situacao_cadastral], status: (e) => e.status },
    (a, b) => Number(Boolean(b.principal)) - Number(Boolean(a.principal)),
  );
  const conta = (s: string) => c.entidades.filter((e) => e.status === s).length;
  return (
    <section data-teste="cadastro-cliente">
      <div className="titulo-pag-linha">
        <h2 className="sem-margem">Entidades legais</h2>
        {c.observacoes && <span className="linha-totais" title={c.observacoes}>Observações do grupo: {c.observacoes}</span>}
        <span className="espaco" />
        {podeEditar && (
          <button type="button" className="botao-primario" onClick={aoNova} data-teste="adicionar-entidade-cliente">
            <Icone nome="mais" /> Adicionar filial / entidade legal
          </button>
        )}
      </div>
      <BarraFiltros
        teste="entidades"
        direita={pers.controles}
        fixos={
          <>
            <label>
              Status no HUB
              <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} data-teste="filtro-status-entidade">
                <option value="ativo">Ativas ({conta('ativo')})</option>
                <option value="inativo">Inativas ({conta('inativo')})</option>
                <option value="">Todas ({c.entidades.length})</option>
              </select>
            </label>
            <label>
              Tipo
              <select value={tipo} onChange={(e) => setTipo(e.target.value)} data-teste="filtro-tipo-entidade">
                <option value="">Todos</option>
                {TIPOS_ENTIDADE.map((t) => (
                  <option key={t} value={t}>
                    {ROTULO_TIPO_ENTIDADE[t]}
                  </option>
                ))}
              </select>
            </label>
          </>
        }
        aoLimpar={() => (setStatus('ativo'), setTipo(''))}
      />
      <div className="rolagem quadro-tabela primeira-fixa duas-fixas">
        <table ref={pers.tabela} className="tabela tabela-entidades" data-teste="entidades-cliente">
          <thead>
            <tr>
              <th className="col-expandir" aria-label="Detalhes" />
              <Th ord={ord} coluna="codigo">Código</Th>
              <Th ord={ord} coluna="tipo">Tipo</Th>
              <Th ord={ord} coluna="razao" className="col-razao">Razão social</Th>
              <Th>CNPJ</Th>
              <Th ord={ord} coluna="cidade">Cidade/UF</Th>
              <Th ord={ord} coluna="situacao">Situação cadastral</Th>
              <Th ord={ord} coluna="status">Status no HUB</Th>
              <Th numero>Ações</Th>
            </tr>
          </thead>
          <tbody>
            {lista.map((e) => {
              const contatos = c.contatos.filter((k) => k.entidade_id === e.id);
              const aberto = aberta === e.id;
              return (
                <Fragment key={e.id}>
                  <tr className={aberto ? 'linha-aberta' : undefined} data-teste={`entidade-${e.tipo}`}>
                    <td className="col-expandir">
                      <button type="button" className="botao-expandir" aria-expanded={aberto} aria-label={`${aberto ? 'Recolher' : 'Ver'} detalhes de ${e.codigo ?? e.cnpj}`} onClick={() => setAberta(aberto ? null : e.id)} data-teste={`expandir-entidade-${e.id}`}>
                        {aberto ? '▾' : '▸'}
                      </button>
                    </td>
                    <td className="nowrap">
                      <span data-teste="codigo-entidade" title={e.codigo ? 'Código da entidade (CLI do Cliente + estabelecimento do CNPJ)' : 'Estabelecimento repetido neste Cliente: código aguardando decisão humana'}>
                        {e.codigo ?? 'código a definir'}
                      </span>
                    </td>
                    <td>
                      {ROTULO_TIPO_ENTIDADE[e.tipo]}
                      {e.principal && (
                        <span className={`sub-principal${e.principal_provisoria ? ' provisoria' : ''}`} data-teste="selo-principal" title={e.principal_provisoria ? 'Sem estabelecimento /0001 na base: principal provisória até a Matriz ser identificada' : 'Entidade padrão do Cliente'}>
                          {e.principal_provisoria ? 'Principal provisória' : 'Principal'}
                        </span>
                      )}
                    </td>
                    <td className="col-razao">
                      <span className="duas-linhas" title={e.razao_social}>
                        {e.razao_social}
                      </span>
                    </td>
                    <td className="nowrap">
                      <span data-teste="cnpj-entidade">{e.cnpj}</span>
                    </td>
                    <td>
                      {e.cidade}/{e.uf}
                    </td>
                    <td>
                      <SeloSituacao texto={ROTULO_SITUACAO_CADASTRAL[e.situacao_cadastral]} tom={e.situacao_cadastral === 'ativa' ? 'positivo' : e.situacao_cadastral === 'suspensa' ? 'alerta' : 'encerrado'} />
                    </td>
                    <td>
                      <SeloSituacao texto={rotuloStatus(e.status, true)} tom={e.status === 'ativo' ? 'positivo' : 'encerrado'} />
                    </td>
                    <td className="numero">
                      <div className="acoes-linha">
                        {podeEditar && (
                          <button type="button" className="botao-secundario pequeno" onClick={() => aoEditar(e)} data-teste={`editar-entidade-${e.tipo}`}>
                            Editar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {aberto && (
                    <tr className="linha-detalhe">
                      <td colSpan={9}>
                        <div className="grade-detalhe">
                          <div>
                            <div className="rotulo-detalhe">Nome fantasia</div>
                            {e.nome_fantasia || '—'}
                          </div>
                          <div>
                            <div className="rotulo-detalhe">Endereço oficial</div>
                            {enderecoTexto(e)}
                          </div>
                          <div>
                            <div className="rotulo-detalhe">Inscrição estadual</div>
                            {e.ie || '—'} ({ROTULO_IE[e.ie_situacao]})
                          </div>
                          <div>
                            <div className="rotulo-detalhe">Contatos relacionados</div>
                            <div className="chips-contatos">
                              {contatos.length
                                ? contatos.map((k) => (
                                    <a key={k.id} className="chip-contato" href={`#/contatos/${k.id}`} title={`Abrir o cadastro de ${k.nome}`} data-teste={`contato-${k.id}`}>
                                      {k.nome}
                                      {k.principal ? <span aria-label=" (contato principal)"> ★</span> : ''} <small>{k.cargo}</small>
                                    </a>
                                  ))
                                : '—'}
                            </div>
                          </div>
                          <div className="acoes-detalhe">
                            {podeEditar && e.status === 'ativo' && (!e.principal || e.principal_provisoria) && (
                              <button type="button" className="botao-secundario pequeno" onClick={() => aoPrincipal(e)} data-teste={`definir-principal-${e.id}`}>
                                {e.principal ? 'Confirmar como principal' : 'Definir como principal'}
                              </button>
                            )}
                            {podeTransferir && !e.principal && !e.com_negocios && (
                              <button type="button" className="botao-secundario pequeno" onClick={() => aoTransferir(e)} data-teste={`transferir-entidade-${e.id}`}>
                                Transferir para outro Cliente
                              </button>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
        <div className="rodape-tabela">{lista.length === 1 ? '1 entidade' : `${lista.length} entidades`}</div>
      </div>
      {!!c.aliases?.length && (
        <p className="texto-secundario" data-teste="aliases-cliente">
          Códigos legados (pesquisáveis): {c.aliases.map((a) => `${a.codigo}${a.entidade_codigo ? ` → ${a.entidade_codigo}` : ''}`).join(' · ')}
        </p>
      )}
    </section>
  );
}

/** F1-19: aba Marketing — dados do grupo e perfil confirmado, sem "Temas de e-mail" e "Prioridade" (dados preservados, só não exibidos). */
/** P2-24 · disposição A: Perfil do cliente; Origem e aderência; Comportamento comercial (D3). Os 9 campos manuais seguem como hoje. */
function MarketingDoCliente({ cliente: c, aoVerPerdas }: { cliente: Cliente; aoVerPerdas: () => void }) {
  const indicacao = indicacaoDe(c);
  const campo = (rotulo: string, valor: ReactNode, teste?: string) => (
    <div className="campo-marketing" data-teste={teste}>
      <dt>{rotulo}</dt>
      <dd>{valor || <span className="ausente">não informado</span>}</dd>
    </div>
  );
  return (
    <div className="marketing-cliente" data-teste="perfil-marketing">
      <section className="bloco-marketing" aria-labelledby="t-perfil">
        <h2 id="t-perfil">
          <span className="indicador-icone c-az" aria-hidden="true">
            <Icone nome="usuario" />
          </span>
          Perfil do cliente
        </h2>
        <dl className="grade-marketing tres">
          {campo('Marca', c.marca, 'confirmado-marca')}
          {campo('Segmento', c.segmento, 'confirmado-segmento')}
          {campo('Subsegmento', c.subsegmento, 'confirmado-subsegmento')}
          {campo(ROTULOS_PERFIL.perfil_empresa, c.perfil_empresa, 'confirmado-perfil_empresa')}
          {campo(ROTULOS_PERFIL.porte_estimado, c.porte_estimado, 'confirmado-porte_estimado')}
        </dl>
      </section>
      <section className="bloco-marketing" aria-labelledby="t-origem">
        <h2 id="t-origem">
          <span className="indicador-icone c-vd" aria-hidden="true">
            <Icone nome="alvo" />
          </span>
          Origem e aderência
        </h2>
        <dl className="grade-marketing duas">
          {/* indicação simples: "Indicação · Gama Distribuição" */}
          {campo('Origem', c.origem ? [c.origem, c.origem === 'Indicação' && indicacao?.nome].filter(Boolean).join(' · ') : '', 'origem-cliente')}
          {campo(ROTULOS_PERFIL.solucoes_maior, c.solucoes_maior, 'confirmado-solucoes_maior')}
          {campo(ROTULOS_PERFIL.solucoes_secundaria, c.solucoes_secundaria, 'confirmado-solucoes_secundaria')}
          {campo(ROTULOS_PERFIL.potencial_cross_sell, c.potencial_cross_sell, 'confirmado-potencial_cross_sell')}
        </dl>
      </section>
      <ComportamentoComercial cliente={c} aoVerPerdas={aoVerPerdas} />
    </div>
  );
}

const DATAS_MKT = [{ id: 'fechamento' as const, rotulo: 'Data de cada registro' }];
const ROTULO_ESTAGIO_MKT = (e: string) => (estagioAberto(e as Estagio) ? 'Em aberto' : e === 'ganha' ? 'Ganha' : e === 'ganha_parcialmente' ? 'Ganha parcialmente' : e === 'perdida' ? 'Perdida' : e === 'cancelada' ? 'Cancelada' : '—');
/** P2-25 · D3: inteligência comercial a partir do que já está gravado (servidor calcula; nada é projetado). */
function ComportamentoComercial({ cliente: c, aoVerPerdas }: { cliente: Cliente; aoVerPerdas: () => void }) {
  const [entidadeId, setEntidadeId] = useState('');
  const [periodo, setPeriodo] = useState<Periodo>(() => periodoInicial(hojeIso()));
  const r = intervalo(periodo, hojeIso());
  const carga = useCarga(() => api.get<ComportamentoComercialDados>(`/api/clientes/${c.id}/comportamento${consulta({ entidade_id: entidadeId, de: r.de ?? '', ate: r.ate ?? '' })}`), [c.id, entidadeId, r.de, r.ate]);
  const d = carga.dados;
  const nada = (x: string) => <span className="texto-secundario">{x}</span>;
  const rank = (x: ComportamentoComercialDados['produtoMaisCotado'], un: [string, string]) => (x ? { valor: x.nome, detalhe: `${x.quantidade} ${x.quantidade === 1 ? un[0] : un[1]}` } : { valor: '—', detalhe: 'sem registros no recorte' });
  return (
    <section className="bloco-marketing" aria-labelledby="t-comportamento" data-teste="comportamento-comercial">
      <div className="cabecalho-bloco-marketing">
        <h2 id="t-comportamento">
          <span className="indicador-icone c-rx" aria-hidden="true">
            <Icone nome="barras" />
          </span>
          Comportamento comercial
          <Ajuda titulo="Comportamento comercial">Cotado: oportunidades distintas em que o produto ou fabricante aparece numa versão da proposta. Comprado: Pedidos não cancelados. Empates: maior valor e, depois, nome. Margem das compras sem o saldo cancelado. Motivo de perda: entre as Perdidas do recorte. Sem período, a frequência considera os últimos 12 meses; com período, conta as compras do período — sempre quantidade real, sem projeção.</Ajuda>
        </h2>
        <span className="espaco" />
        {c.entidades.length > 1 && (
          <label>
            Filial/unidade
            <select value={entidadeId} onChange={(e) => setEntidadeId(e.target.value)} data-teste="filtro-entidade-marketing">
              <option value="">Todas</option>
              {c.entidades.map((e) => (
                <option key={e.id} value={e.id}>
                  {rotuloEntidade(e)}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {/* revisão 12/10: todos os períodos aprovados (inclui Trimestre, Ano e Personalizado) numa linha própria, com as datas do recorte visíveis */}
      <div className="periodo-marketing">
        <FiltroPeriodo periodo={periodo} setPeriodo={setPeriodo} datas={DATAS_MKT} data="fechamento" setData={() => {}} semData teste="periodo-marketing" />
        {r.de && r.ate && (
          <span className="texto-secundario recorte-marketing" data-teste="periodo-marketing-datas">
            Recorte: {r.rotulo}
          </span>
        )}
      </div>
      <EstadoCarga carregando={carga.carregando && !d} erro={carga.erro} recarregar={carga.recarregar} teste="comportamento" />
      {d && (
        <>
          <ResumoIndicadores
            teste="indicadores-comportamento"
            itens={[
              { rotulo: 'Produto mais cotado', ...rank(d.produtoMaisCotado, ['oportunidade', 'oportunidades']), icone: 'etiqueta', teste: 'mkt-produto-cotado' },
              { rotulo: 'Fabricante mais cotado', ...rank(d.fabricanteMaisCotado, ['oportunidade', 'oportunidades']), icone: 'predio', teste: 'mkt-fabricante-cotado' },
              { rotulo: 'Produto mais comprado', ...rank(d.produtoMaisComprado, ['pedido', 'pedidos']), icone: 'pedidos', teste: 'mkt-produto-comprado' },
              { rotulo: 'Fabricante mais comprado', ...rank(d.fabricanteMaisComprado, ['pedido', 'pedidos']), icone: 'predio', teste: 'mkt-fabricante-comprado' },
              {
                rotulo: 'Última compra',
                valor: d.ultimaCompra ? dataBr(d.ultimaCompra.data) : '—',
                detalhe: d.ultimaCompra ? <a href={`#/pedidos/${d.ultimaCompra.pedido}`}>{d.ultimaCompra.pedido}</a> : 'nenhuma compra no recorte',
                icone: 'relogio',
                teste: 'mkt-ultima-compra',
              },
              {
                // D3: quantidade real — sem período, "últimos 12 meses"; com período, as datas
                rotulo: d.compras.noPeriodo ? 'Compras no período' : 'Frequência de compras',
                valor: `${d.compras.quantidade} ${d.compras.quantidade === 1 ? 'compra' : 'compras'}`,
                detalhe: d.compras.noPeriodo ? `em ${dataBr(d.compras.de)} a ${dataBr(d.compras.ate)}` : 'últimos 12 meses',
                icone: 'barras',
                teste: 'mkt-compras',
              },
              { rotulo: 'Categoria mais comprada', ...rank(d.categoriaMaisComprada, ['pedido', 'pedidos']), icone: 'produtos', teste: 'mkt-categoria' },
              { rotulo: 'Margem das compras', valor: moeda(d.margem.valor), detalhe: d.margem.percentual === null ? '—' : pct(d.margem.percentual), icone: 'pizza', teste: 'mkt-margem' },
            ]}
          />
          <div className={`faixa-perda${d.motivoPerda ? '' : ' vazia'}`} data-teste="mkt-motivo-perda">
            <span className="indicador-icone c-lj" aria-hidden="true">
              <Icone nome="queda" />
            </span>
            <div>
              <span className="rotulo-perda">Principal motivo de perda</span>
              {d.motivoPerda ? (
                <span>
                  <b>{d.motivoPerda.motivo}</b> · {d.motivoPerda.quantidade} de {d.motivoPerda.total} {d.motivoPerda.total === 1 ? 'perda' : 'perdas'} · {pct(d.motivoPerda.percentual)}
                </span>
              ) : (
                nada('nenhuma oportunidade perdida no recorte')
              )}
            </div>
            <span className="espaco" />
            {d.motivoPerda && (
              <button type="button" className="botao-link" onClick={aoVerPerdas} data-teste="ver-perdas">
                Ver oportunidades →
              </button>
            )}
          </div>
          <h3 className="titulo-nao-comprados">Produtos cotados e ainda não comprados</h3>
          {d.cotadosNaoComprados.length === 0 ? (
            <p className="vazio" data-teste="mkt-nao-comprados-vazio">
              Nenhum produto cotado sem compra no recorte.
            </p>
          ) : (
            <div className="rolagem quadro-tabela">
              <table className="tabela tabela-compacta" data-teste="mkt-nao-comprados">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th>Última cotação</th>
                    <th>Status</th>
                    <th>Oportunidade</th>
                  </tr>
                </thead>
                <tbody>
                  {d.cotadosNaoComprados.map((x) => (
                    <tr key={x.produto}>
                      <td>{x.produto}</td>
                      <td className="nowrap">{dataBr(x.ultimaCotacao)}</td>
                      <td>
                        <SeloSituacao texto={ROTULO_ESTAGIO_MKT(x.estagio)} tom={estagioAberto(x.estagio as Estagio) ? 'positivo' : x.estagio === 'perdida' ? 'encerrado' : 'aberto'} />
                      </td>
                      <td>
                        <a href={`#/oportunidades/${x.oportunidade.id}`}>#{x.oportunidade.numero}</a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </section>
  );
}

type ColProp = 'proposta' | 'oportunidade' | 'tipo' | 'versao' | 'situacao' | 'valor' | 'margem' | 'validade' | 'pedido';
const TOM_VERSAO: Record<SituacaoVersao, 'aberto' | 'positivo' | 'encerrado' | 'alerta' | 'parcial'> = { rascunho: 'alerta', enviada: 'aberto', em_negociacao: 'aberto', aceita: 'positivo', aceita_parcialmente: 'parcial', recusada: 'encerrado', expirada: 'encerrado', substituida: 'encerrado' };
/** Aba Propostas (nova): uma proposta por Oportunidade, com a versão vigente. */
function PropostasDoCliente({ cliente: c, entidadeId }: { cliente: Cliente; entidadeId?: string }) {
  const carga = useDadosOportunidades(c.id, entidadeId);
  const ord = useOrdenacao<ColProp>('proposta', 'desc');
  const pers = usePersonalizacao('cliente-propostas', { ordenacao: ord });
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="propostas-cliente" />;
  const comProposta = carga.dados.lista.filter((o) => o.proposta);
  const lista = ord.ordenar(comProposta, {
    proposta: (o) => o.proposta!.numero,
    oportunidade: (o) => o.titulo,
    tipo: (o) => TIPOS[o.tipo],
    versao: (o) => o.proposta!.versao,
    situacao: (o) => o.proposta!.situacao,
    valor: (o) => o.valor_vigente,
    margem: (o) => o.margem?.percentual,
    validade: (o) => o.proposta!.validade,
    pedido: (o) => o.pedidos[0],
  });
  if (!lista.length) return <p className="vazio" data-teste="propostas-cliente-vazia">Nenhuma proposta deste cliente.</p>;
  return (
    <section data-teste="aba-propostas-cliente">
      <LinhaPersonalizacao>{pers.controles}</LinhaPersonalizacao>
      <div className="rolagem quadro-tabela primeira-fixa">
        <table ref={pers.tabela} className="tabela" data-teste="propostas-cliente">
          <thead>
            <tr>
              <Th ord={ord} coluna="proposta" padrao="desc">Proposta</Th>
              <Th ord={ord} coluna="oportunidade">Oportunidade</Th>
              <Th ord={ord} coluna="tipo">Tipo</Th>
              <Th ord={ord} coluna="versao">Versão vigente</Th>
              <Th ord={ord} coluna="situacao">Status</Th>
              <Th ord={ord} coluna="valor" padrao="desc" numero>Valor</Th>
              <Th ord={ord} coluna="margem" padrao="desc" numero>Margem (%)</Th>
              <Th ord={ord} coluna="validade">Validade</Th>
              <Th ord={ord} coluna="pedido">Pedido</Th>
            </tr>
          </thead>
          <tbody>
            {lista.map((o) => {
              const p = o.proposta!;
              const sit = { texto: SITUACOES_VERSAO[p.situacao as SituacaoVersao] ?? p.situacao, tom: TOM_VERSAO[p.situacao as SituacaoVersao] ?? 'aberto' };
              return (
                <tr key={o.id}>
                  <td className="nowrap">
                    <a href={`#/oportunidades/${o.id}/propostas/${p.id}/v/${p.versao}`}>{p.numero}</a>
                  </td>
                  <td>
                    <a href={`#/oportunidades/${o.id}`}>{o.titulo}</a>
                    <div className="texto-secundario">#{o.numero}</div>
                  </td>
                  <td>
                    <SeloTipo tipo={o.tipo} />
                  </td>
                  <td className="nowrap">v{p.versao}</td>
                  <td>
                    <SeloSituacao texto={sit.texto} tom={sit.tom} />
                  </td>
                  <td className="nowrap numero">{moeda(o.valor_vigente ?? 0)}</td>
                  <td className="nowrap numero">{o.margem ? pct(o.margem.percentual) : <span className="ausente">—</span>}</td>
                  <td className="nowrap">{p.validade ? dataBr(p.validade) : <span className="ausente">—</span>}</td>
                  <td className="nowrap">{o.pedidos[0] ? <a href={`#/pedidos/${o.pedidos[0]}`}>{o.pedidos[0]}</a> : <span className="ausente">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="rodape-tabela">{lista.length} proposta(s)</div>
      </div>
    </section>
  );
}

type ColOrdemCliente = 'numero' | 'tipo' | 'entidade' | 'pedido' | 'criacao' | 'situacao' | 'valor';
/** Aba Ordens de compra (nova): OCF, OL e OTI do Cliente (consolidado). */
function OrdensDoCliente({ cliente: c }: { cliente: Cliente }) {
  const carga = useCarga(() => api.get<{ ocf: OcfResumo[]; documentos: DocResumo[] }>('/api/ordens'), []);
  const ord = useOrdenacao<ColOrdemCliente>('criacao', 'desc');
  const pers = usePersonalizacao('cliente-ordens', { ordenacao: ord });
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="ordens-cliente" />;
  type Linha = { id: string; numero: string; tipo: 'venda' | 'locacao' | 'trade_in'; entidade: string; cnpj: string; pedido: string; criacao: string; situacao: string; tom: 'aberto' | 'positivo' | 'parcial' | 'encerrado' | 'backlog' | 'alerta'; valor: number };
  const linhas: Linha[] = [
    ...carga.dados.ocf.filter((o) => o.cliente.id === c.id).map((o) => ({ id: o.id, numero: o.numero, tipo: 'venda' as const, entidade: o.entidade.fantasia, cnpj: o.entidade.cnpj, pedido: o.pedido.numero, criacao: o.dataCriacao, situacao: ESTADOS_ORDEM[o.situacao], tom: tomOrdem(o.situacao), valor: o.valores.venda })),
    ...carga.dados.documentos.filter((d) => d.cliente.id === c.id).map((d) => ({ id: d.id, numero: d.numero, tipo: d.tipo, entidade: d.entidade?.fantasia ?? '—', cnpj: d.entidade?.cnpj ?? '', pedido: d.pedido.numero, criacao: d.dataCriacao, situacao: d.situacaoTexto, tom: (d.situacao === 'cancelada' ? 'encerrado' : d.situacao === 'emitida' ? 'positivo' : 'aberto') as Linha['tom'], valor: d.valores.valorCliente })),
  ];
  const lista = ord.ordenar(linhas, { numero: (x) => x.numero, tipo: (x) => x.tipo, entidade: (x) => x.entidade, pedido: (x) => x.pedido, criacao: (x) => x.criacao, situacao: (x) => x.situacao, valor: (x) => x.valor }, (a, b) => b.numero.localeCompare(a.numero));
  if (!lista.length) return <p className="vazio" data-teste="ordens-cliente-vazia">Nenhuma ordem deste cliente.</p>;
  return (
    <section data-teste="aba-ordens-cliente">
      <LinhaPersonalizacao>{pers.controles}</LinhaPersonalizacao>
      <div className="rolagem quadro-tabela primeira-fixa">
        <table ref={pers.tabela} className="tabela" data-teste="ordens-cliente">
          <thead>
            <tr>
              <Th ord={ord} coluna="numero">Número</Th>
              <Th ord={ord} coluna="tipo">Tipo de ordem</Th>
              <Th ord={ord} coluna="entidade">Entidade faturadora (fornecedor)</Th>
              <Th ord={ord} coluna="pedido">Pedido</Th>
              <Th ord={ord} coluna="criacao" padrao="desc">Criação</Th>
              <Th ord={ord} coluna="situacao">Status</Th>
              <Th ord={ord} coluna="valor" padrao="desc" numero>Valor</Th>
            </tr>
          </thead>
          <tbody>
            {lista.map((x) => (
              <tr key={x.id}>
                <td className="nowrap">
                  <a href={`#/ordens/${x.numero}`}>{x.numero}</a>
                </td>
                <td>
                  <SeloOrdem tipo={x.tipo} />
                </td>
                <td>
                  {x.entidade}
                  {x.cnpj && <div className="texto-secundario nowrap">{x.cnpj}</div>}
                </td>
                <td className="nowrap">
                  <a href={`#/pedidos/${x.pedido}`}>{x.pedido}</a>
                </td>
                <td className="nowrap">{dataBr(x.criacao)}</td>
                <td>
                  <SeloSituacao texto={x.situacao} tom={x.tom} />
                </td>
                <td className="nowrap numero">{moeda(x.valor)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="rodape-tabela">{lista.length} ordem(ns)</div>
      </div>
    </section>
  );
}

/** Aba Oportunidades do Cliente: a MESMA Lista da tela Oportunidades, com os mesmos filtros, só com as deste Cliente. */
function OportunidadesDoCliente({ cliente: c, podeCriar, entidadeId, filial, aoLimparFilial }: { cliente: Cliente; podeCriar: boolean; entidadeId?: string; filial?: ReactNode; aoLimparFilial?: () => void }) {
  const [nova, setNova] = useState(false);
  const carga = useDadosOportunidades(c.id, entidadeId);
  const f = useFiltrosOportunidades(carga.dados?.opcoes ?? null);
  const { situacoes } = useSituacoes();
  if (!carga.dados) return <EstadoCarga carregando={carga.carregando} erro={carga.erro} recarregar={carga.recarregar} teste="oportunidades-cliente" />;
  const { lista: doCliente, opcoes } = carga.dados;
  // P2-19 (D1): faixa discreta com o valor do que está na lista; o título acompanha o Status escolhido
  const titulo = situacoes.length === 1 && situacoes[0] === 'aberta' ? 'Valor de oportunidades abertas' : situacoes.length > 0 && situacoes.every((x) => x === 'ganha' || x === 'ganha_parcialmente') ? 'Valor de oportunidades ganhas' : situacoes.length === 1 && situacoes[0] === 'perdida' ? 'Valor de oportunidades perdidas' : situacoes.length === 1 && situacoes[0] === 'cancelada' ? 'Valor de oportunidades canceladas' : 'Valor das oportunidades selecionadas';
  const faixa = (itens: OportunidadeResumo[]) => (
    <div className="faixa-valor" data-teste="faixa-valor-oportunidades">
      <span data-teste="faixa-valor-titulo">{titulo}</span>
      <b data-teste="faixa-valor">{moeda(itens.reduce((s, o) => s + valorReferencia(o), 0))}</b>
      <span className="espaco" />
      {podeCriar && (
        <button type="button" className="botao-primario" onClick={() => setNova(true)} data-teste="nova-oportunidade-cliente">
          Nova oportunidade
        </button>
      )}
    </div>
  );
  return (
    <section data-teste="aba-oportunidades-cliente">
      <ListaOportunidades lista="cliente-oportunidades" base={f.aplicar(doCliente)} baseBusca={f.aplicar(doCliente, true)} opcoes={opcoes} filtros={f} ocultarCliente teste="oportunidades-cliente" totalTeste="total-oportunidades-cliente" topo={() => null} filial={filial} aoLimparFilial={aoLimparFilial} faixa={faixa} />
      {nova && <ModalNovaOportunidade cliente={c} responsaveis={opcoes.responsaveis} aoFechar={() => setNova(false)} />}
    </section>
  );
}

/** Aba Contatos do Cliente: D8 — sem busca própria (o filtro Filial/unidade do Cliente continua valendo); acesso ao cadastro do contato. */
function ContatosDoCliente({ cliente: c, aoEditar, aoNovo, podeEditar }: { cliente: Cliente; aoEditar: (k: Contato) => void; aoNovo?: () => void; podeEditar: boolean }) {
  const lista = c.contatos;
  const pers = usePersonalizacao('cliente-contatos');
  return (
    <section data-teste="aba-contatos-cliente">
      <div className="barra-lista">
        <span className="texto-secundario">Contatos das matrizes e filiais deste Cliente. Clique no nome para abrir o cadastro.</span>
        {aoNovo && (
          <button type="button" className="botao-primario" onClick={aoNovo} data-teste="novo-contato">
            Novo contato
          </button>
        )}
      </div>
      <LinhaPersonalizacao>{pers.controles}</LinhaPersonalizacao>
      <div className="rolagem primeira-fixa">
        <table ref={pers.tabela} className="tabela tabela-compacta" data-teste="contatos">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Matriz/filial</th>
              <th>Cargo/função</th>
              <th>E-mail</th>
              <th>Telefone</th>
              <th>Principal</th>
              <th>Papel na negociação</th>
              <th>Status</th>
              <th className="col-acoes">Ação</th>
            </tr>
          </thead>
          <tbody>
            {lista.map((k) => {
              const e = c.entidades.find((x) => x.id === k.entidade_id)!;
              return (
                <tr key={k.id}>
                  <td>
                    <a href={`#/contatos/${k.id}`}>{k.nome}</a>
                  </td>
                  <td>
                    {ROTULO_TIPO_ENTIDADE[e.tipo]}: {e.nome_fantasia || e.razao_social}
                    <div className="texto-secundario nowrap">{e.cnpj}</div>
                  </td>
                  <td>
                    {k.cargo}
                    <div className="texto-secundario">{k.departamento}</div>
                  </td>
                  <td>{k.email || '—'}</td>
                  <td className="nowrap">
                    {k.telefone}
                    {k.whatsapp && k.whatsapp !== k.telefone && <div className="texto-secundario">WhatsApp {k.whatsapp}</div>}
                  </td>
                  <td>{k.principal ? '★ principal' : '—'}</td>
                  <td>{k.papel}</td>
                  <td>{rotuloStatus(k.status)}</td>
                  <td className="col-acoes">
                    {podeEditar && (
                      <button type="button" className="botao-secundario pequeno" onClick={() => aoEditar(k)}>
                        Editar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {lista.length === 0 && (
              <tr>
                <td colSpan={9} className="texto-secundario">
                  Nenhum contato cadastrado.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ---------------- entidade legal (adicionar / editar) ---------------- */

interface FormEntidade {
  tipo: Entidade['tipo'];
  cnpj: string;
  razao_social: string;
  nome_fantasia: string;
  ie: string;
  ie_situacao: SituacaoIe;
  situacao_cadastral: SituacaoCadastral;
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  uf: string;
  status: Entidade['status'];
}
export const entidadeVazia = (tipo: Entidade['tipo']): FormEntidade => ({ tipo, cnpj: '', razao_social: '', nome_fantasia: '', ie: '', ie_situacao: 'ativa', situacao_cadastral: 'ativa', cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: '', status: 'ativo' });
const formDe = (e: Entidade): FormEntidade => ({ tipo: e.tipo, cnpj: e.cnpj, razao_social: e.razao_social, nome_fantasia: e.nome_fantasia ?? '', ie: e.ie ?? '', ie_situacao: e.ie_situacao, situacao_cadastral: e.situacao_cadastral, cep: e.cep, logradouro: e.logradouro ?? '', numero: e.numero ?? '', complemento: e.complemento ?? '', bairro: e.bairro ?? '', cidade: e.cidade, uf: e.uf, status: e.status });

/** Campos da entidade legal (os mesmos em Novo cliente, Adicionar filial e Editar). CNPJ só na inclusão. */
export function CamposEntidade({ e, set, cnpjEditavel, prefixo, aceitarFicticio }: { e: FormEntidade; set: (patch: Partial<FormEntidade>) => void; cnpjEditavel: boolean; prefixo: string; aceitarFicticio: boolean }) {
  // CEP completo e alterado → sugestão de Logradouro, Bairro, Município e UF (editáveis); o Número continua manual
  const { situacao: consultaCep, marcarConsultado: cepJaPreenchido } = useConsultaCep(e.cep, (end) => set(end));
  // CNPJ completo e válido na inclusão → sugestão dos dados da Receita (editáveis); a IE continua manual
  const consultaCnpj = useConsultaCnpj(e.cnpj, cnpjEditavel, (d: DadosCnpjTela) => {
    const patch: Partial<FormEntidade> = { razao_social: d.razaoSocial };
    if (d.nomeFantasia) patch.nome_fantasia = d.nomeFantasia;
    if (d.situacaoCadastral) patch.situacao_cadastral = d.situacaoCadastral; // as 5 situações do domínio fechado
    if (d.matriz !== null) patch.tipo = d.matriz ? 'matriz' : 'filial';
    const end = d.endereco;
    if (end.cep) {
      const c = validarCep(end.cep);
      patch.cep = c.ok ? c.formatado : end.cep;
      cepJaPreenchido(end.cep); // o endereço da Receita não é sobrescrito pela consulta de CEP
    }
    for (const [campo, valor] of [['logradouro', end.logradouro], ['numero', end.numero], ['complemento', end.complemento], ['bairro', end.bairro], ['cidade', end.cidade], ['uf', end.uf]] as const) if (valor) patch[campo] = valor;
    set(patch);
  });
  return (
    <>
      {cnpjEditavel ? (
        <>
          <CampoCnpj valor={e.cnpj} aoMudar={(v) => set({ cnpj: v })} teste={`cnpj-${prefixo}`} obrigatorio aceitarFicticio={aceitarFicticio} ajuda={AJUDA_CNPJ_CONSULTA} />
          {consultaCnpj && (
            <div className="largura-total" role="status" aria-live="polite" data-teste={`cnpj-consulta-${prefixo}`} data-situacao={consultaCnpj.tipo}>
              <p className={`sem-margem ${consultaCnpj.tipo === 'falhou' ? 'aviso' : 'texto-secundario'}`}>{consultaCnpj.texto}</p>
              {consultaCnpj.alerta && (
                <p className="aviso aviso-alerta sem-margem" data-teste={`cnpj-alerta-${prefixo}`}>
                  ⚠ {consultaCnpj.alerta}
                </p>
              )}
            </div>
          )}
        </>
      ) : (
        <label>
          CNPJ
          <input value={e.cnpj} disabled aria-describedby={`${prefixo}-cnpj-fixo`} />
          <small id={`${prefixo}-cnpj-fixo`} className="ajuda">
            O CNPJ não muda: para corrigir, inative esta entidade e cadastre a correta.
          </small>
        </label>
      )}
      <label className="coluna-dupla">
        Razão social *
        <input value={e.razao_social} onChange={(x) => set({ razao_social: x.target.value })} data-teste={`razao-${prefixo}`} />
      </label>
      <label>
        Nome fantasia
        <input value={e.nome_fantasia} onChange={(x) => set({ nome_fantasia: x.target.value })} data-teste={`fantasia-${prefixo}`} />
      </label>
      <label>
        Inscrição estadual
        <input value={e.ie} onChange={(x) => set({ ie: x.target.value })} />
      </label>
      <label>
        Situação da inscrição estadual
        <select value={e.ie_situacao} onChange={(x) => set({ ie_situacao: x.target.value as SituacaoIe })} data-teste={`ie-situacao-${prefixo}`}>
          {(Object.keys(ROTULO_IE) as SituacaoIe[]).map((s) => (
            <option key={s} value={s}>
              {ROTULO_IE[s]}
            </option>
          ))}
        </select>
      </label>
      <label>
        Situação cadastral
        <select value={e.situacao_cadastral} onChange={(x) => set({ situacao_cadastral: x.target.value as SituacaoCadastral })} data-teste={`situacao-cadastral-${prefixo}`}>
          {(Object.keys(ROTULO_SITUACAO_CADASTRAL) as SituacaoCadastral[]).map((s) => (
            <option key={s} value={s}>
              {ROTULO_SITUACAO_CADASTRAL[s]}
            </option>
          ))}
        </select>
      </label>
      <label>
        Status no HUB
        <select value={e.status} onChange={(x) => set({ status: x.target.value as Entidade['status'] })}>
          <option value="ativo">Ativa</option>
          <option value="inativo">Inativa</option>
        </select>
      </label>
      <div className="subtitulo-form largura-total">Endereço oficial</div>
      <CampoCep valor={e.cep} aoMudar={(v) => set({ cep: v })} teste={`cep-${prefixo}`} ajuda={AJUDA_CEP_CONSULTA} />
      {consultaCep && (
        <p className={`largura-total sem-margem ${consultaCep.tipo === 'falhou' ? 'aviso' : 'texto-secundario'}`} role="status" aria-live="polite" data-teste={`cep-consulta-${prefixo}`} data-situacao={consultaCep.tipo}>
          {consultaCep.texto}
        </p>
      )}
      <label className="coluna-dupla">
        Logradouro
        <input value={e.logradouro} onChange={(x) => set({ logradouro: x.target.value })} data-teste={`logradouro-${prefixo}`} />
      </label>
      <label>
        Número
        <input value={e.numero} onChange={(x) => set({ numero: x.target.value })} />
      </label>
      <label>
        Complemento
        <input value={e.complemento} onChange={(x) => set({ complemento: x.target.value })} />
      </label>
      <label>
        Bairro
        <input value={e.bairro} onChange={(x) => set({ bairro: x.target.value })} />
      </label>
      <label>
        Cidade *
        <input value={e.cidade} onChange={(x) => set({ cidade: x.target.value })} data-teste={`cidade-${prefixo}`} />
      </label>
      <SeletorUf valor={e.uf} aoMudar={(v) => set({ uf: v })} teste={`uf-${prefixo}`} rotulo="UF *" />
    </>
  );
}

/** Adicionar filial / entidade legal ao Grupo de Cliente (CNPJ único em todo o HUB), ou editar uma existente. */
function ModalEntidadeCliente({ cliente: c, entidade, aoFechar, aoSalvar }: { cliente: Cliente; entidade?: Entidade; aoFechar: () => void; aoSalvar: () => void }) {
  const { aceitarFicticio } = useAmbiente();
  const [e, setE] = useState<FormEntidade>(() => (entidade ? formDe(entidade) : entidadeVazia('filial')));
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const salvar = async () => {
    setErro('');
    setSalvando(true);
    try {
      if (entidade) {
        const { cnpj: _c, ...dados } = e;
        await api.patch(`/api/clientes/${c.id}/entidades/${entidade.id}`, { ...dados, justificativa: justificativa.trim() || undefined, atualizado_em: entidade.atualizado_em });
      } else await api.post(`/api/clientes/${c.id}/entidades`, e);
      aoSalvar();
    } catch (x) {
      setErro((x as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={entidade ? `Editar ${ROTULO_TIPO_ENTIDADE[entidade.tipo]} — ${entidade.razao_social}` : `Adicionar filial / entidade legal — ${c.nome}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" onClick={salvar} disabled={salvando} data-teste="salvar-entidade-cliente">
            {entidade ? 'Salvar entidade' : 'Adicionar entidade'}
          </button>
        </>
      }
    >
      {!entidade && <p className="texto-secundario">A nova entidade passa a fazer parte deste Grupo de Cliente e fica disponível para faturamento e entrega nas Propostas. CNPJ único em todo o HUB.</p>}
      <div className="form-grade sem-borda" data-teste="form-entidade-cliente">
        <label>
          Tipo
          <select value={e.tipo} onChange={(x) => setE({ ...e, tipo: x.target.value as Entidade['tipo'] })} data-teste="tipo-entidade-cliente">
            {TIPOS_ENTIDADE.map((t) => (
              <option key={t} value={t}>
                {ROTULO_TIPO_ENTIDADE[t]}
              </option>
            ))}
          </select>
        </label>
        <CamposEntidade e={e} set={(p) => setE((x) => ({ ...x, ...p }))} cnpjEditavel={!entidade} prefixo="entidade-cliente" aceitarFicticio={aceitarFicticio} />
      </div>
      {entidade && (
        <label className="campo">
          Justificativa{entidade.migrado ? ' (obrigatória: registro migrado da planilha)' : ' (opcional, registrada na auditoria)'}
          <textarea rows={2} value={justificativa} onChange={(x) => setJustificativa(x.target.value)} maxLength={1000} data-teste="justificativa-entidade-cliente" />
        </label>
      )}
      {erro && (
        <p className="aviso aviso-alerta" role="alert" data-teste="faltam-entidade-cliente">
          {erro}
        </p>
      )}
    </Modal>
  );
}

/* ---------------- grupo: editar e inativar ---------------- */

export interface FormGrupo {
  nome: string;
  marca: string;
  segmento: string;
  subsegmento: string;
  responsavel_id: string;
  origem: string;
  observacoes: string;
  perfil: Record<(typeof CAMPOS_PERFIL)[number], string>;
}
export const perfilVazio = () => Object.fromEntries(CAMPOS_PERFIL.map((k) => [k, ''])) as FormGrupo['perfil'];

/** Campos do Grupo de Cliente (Novo cliente e Editar cliente). */
export function CamposGrupo({ g, set, opcoes, indicacao, setIndicacao, clienteId, origemGravada }: { g: FormGrupo; set: (p: Partial<FormGrupo>) => void; opcoes: Opcoes; indicacao?: Indicacao | null; setIndicacao: (i?: Indicacao) => void; clienteId?: string; /** edição: Origem gravada (pode estar fora da lista, ex.: migrados) */ origemGravada?: string }) {
  const texto = (rotulo: string, chave: 'nome' | 'marca' | 'segmento' | 'subsegmento', placeholder?: string) => (
    <label>
      {rotulo}
      <input value={g[chave]} onChange={(e) => set({ [chave]: e.target.value })} placeholder={placeholder} data-teste={`grupo-${chave}`} />
    </label>
  );
  return (
    <>
      {texto('Nome do grupo', 'nome', 'ex.: Grupo Exemplo')}
      {texto('Marca', 'marca')}
      {texto('Segmento', 'segmento')}
      {texto('Subsegmento', 'subsegmento')}
      <label>
        Responsável comercial
        <select value={g.responsavel_id} onChange={(e) => set({ responsavel_id: e.target.value })} data-teste="grupo-responsavel">
          <option value="">Selecione…</option>
          {opcoes.responsaveis.map((r) => (
            <option key={r.id} value={r.id}>
              {r.nome}
            </option>
          ))}
        </select>
      </label>
      <CampoOrigem origens={opcoes.origens} origem={g.origem} setOrigem={(v) => set({ origem: v })} indicacao={indicacao} setIndicacao={setIndicacao} ignorarClienteId={clienteId} atual={origemGravada} />
      {!opcoes.origens_configuradas && (
        <p className="aviso aviso-alerta largura-total" role="alert">
          A lista de Origens do Cliente não está configurada neste ambiente. Peça ao Administrador para configurá-la em Parâmetros.
        </p>
      )}
      <label className="largura-total">
        Observações
        <textarea rows={2} value={g.observacoes} onChange={(e) => set({ observacoes: e.target.value })} />
      </label>
    </>
  );
}

export function CamposPerfil({ perfil, set }: { perfil: FormGrupo['perfil']; set: (p: FormGrupo['perfil']) => void }) {
  return (
    <>
      {CAMPOS_PERFIL.map((k) => (
        <label key={k} className={k === 'temas_email' ? 'largura-total' : undefined}>
          {ROTULOS_PERFIL[k]}
          <input value={perfil[k]} onChange={(e) => set({ ...perfil, [k]: e.target.value })} data-teste={`perfil-campo-${k}`} />
        </label>
      ))}
    </>
  );
}

const indicacaoCorpo = (origem: string, i?: Indicacao | null) => (origem === 'Indicação' && i ? { tipo: i.tipo, id: i.id, nome: i.nome } : undefined);

function ModalEditarCliente({ cliente: c, aoFechar, aoSalvar }: { cliente: Cliente; aoFechar: () => void; aoSalvar: () => void }) {
  const opcoes = useCarga(() => api.get<Opcoes>('/api/clientes/opcoes'), []);
  const [g, setG] = useState<FormGrupo>(() => ({ nome: c.nome, marca: c.marca ?? '', segmento: c.segmento ?? '', subsegmento: c.subsegmento ?? '', responsavel_id: c.responsavel_id, origem: c.origem, observacoes: c.observacoes ?? '', perfil: Object.fromEntries(CAMPOS_PERFIL.map((k) => [k, c[k] ?? ''])) as FormGrupo['perfil'] }));
  const [indicacao, setIndicacao] = useState<Indicacao | undefined>(indicacaoDe(c) ?? undefined);
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);
  const salvar = async () => {
    setErro('');
    setSalvando(true);
    try {
      const { perfil, ...grupo } = g;
      await api.patch(`/api/clientes/${c.id}`, { ...grupo, ...perfil, indicacao: indicacaoCorpo(g.origem, indicacao), justificativa: justificativa.trim() || undefined, atualizado_em: c.atualizado_em });
      aoSalvar();
    } catch (x) {
      setErro((x as Error).message);
    } finally {
      setSalvando(false);
    }
  };
  return (
    <Modal
      titulo={`Editar cliente — ${c.nome}`}
      aoFechar={aoFechar}
      largo
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Cancelar
          </button>
          <button type="button" className="botao-primario" onClick={salvar} disabled={salvando || !opcoes.dados} data-teste="salvar-cliente">
            Salvar cliente
          </button>
        </>
      }
    >
      <EstadoCarga carregando={opcoes.carregando} erro={opcoes.erro} recarregar={opcoes.recarregar} />
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-editar-cliente">
          {erro}
        </p>
      )}
      {opcoes.dados && (
        <>
          <fieldset className="form-grade">
            <legend>Grupo de Cliente</legend>
            <CamposGrupo g={g} set={(p) => setG({ ...g, ...p })} opcoes={opcoes.dados} indicacao={indicacao} setIndicacao={setIndicacao} clienteId={c.id} origemGravada={c.origem ?? undefined} />
          </fieldset>
          <fieldset className="form-grade">
            <legend>Perfil de marketing</legend>
            <CamposPerfil perfil={g.perfil} set={(perfil) => setG({ ...g, perfil })} />
          </fieldset>
          {c.migrado && (
            <label className="campo">
              Justificativa (obrigatória: registro migrado da planilha)
              <textarea rows={2} value={justificativa} onChange={(x) => setJustificativa(x.target.value)} maxLength={1000} data-teste="justificativa-editar-cliente" />
            </label>
          )}
        </>
      )}
    </Modal>
  );
}

/** Define a entidade PRINCIPAL do Cliente (a anterior só perde o atributo: código, histórico e negócios não mudam). */
function ModalDefinirPrincipal({ cliente: c, entidade: e, aoFechar, aoSalvar }: { cliente: Cliente; entidade: Entidade; aoFechar: () => void; aoSalvar: () => void }) {
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const atual = matrizDe(c);
  const confirmar = async () => {
    setErro('');
    try {
      await api.post(`/api/clientes/${c.id}/entidades/${e.id}/principal`, { justificativa });
      aoSalvar();
    } catch (x) {
      setErro((x as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Entidade principal — ${c.nome}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" onClick={confirmar} disabled={!justificativa.trim()} data-teste="confirmar-principal">
            Definir como principal
          </button>
        </>
      }
    >
      <p>
        {atual.id === e.id ? (
          <>Confirma {rotuloEntidade(e)} como a entidade principal (deixa de ser provisória).</>
        ) : (
          <>
            {rotuloEntidade(e)} passa a ser a entidade principal. A atual ({rotuloEntidade(atual)}) só deixa de ser principal: código, histórico e negócios não mudam.
          </>
        )}
      </p>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <label className="campo">
        Justificativa (obrigatória, registrada na auditoria e na Timeline)
        <textarea rows={3} value={justificativa} onChange={(x) => setJustificativa(x.target.value)} maxLength={1000} data-teste="justificativa-principal" />
      </label>
    </Modal>
  );
}

/** Transferir entidade SEM negócios para outro Cliente (código antigo vira alias; contatos sem histórico acompanham). */
function ModalTransferirEntidade({ cliente: c, entidade: e, aoFechar }: { cliente: Cliente; entidade: Entidade; aoFechar: () => void }) {
  const [busca, setBusca] = useState('');
  const [destino, setDestino] = useState('');
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const lista = useCarga(() => api.get<ClienteResumo[]>(`/api/clientes${consulta({ busca: busca.trim(), status: 'ativo' })}`), [busca]);
  const confirmar = async () => {
    setErro('');
    try {
      await api.post(`/api/clientes/${c.id}/entidades/${e.id}/transferir`, { cliente_destino_id: destino, justificativa });
      aoFechar();
      navegar(`/clientes/${destino}`);
    } catch (x) {
      setErro((x as Error).message);
    }
  };
  return (
    <Modal
      titulo={`Transferir entidade — ${rotuloEntidade(e)}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" onClick={confirmar} disabled={!destino || !justificativa.trim()} data-teste="confirmar-transferencia">
            Transferir
          </button>
        </>
      }
    >
      <p className="texto-secundario">Só entidades sem negócios (Oportunidade, Proposta ou Pedido) podem ser transferidas. O código atual vira código legado pesquisável e a entidade recebe o código do Cliente de destino. Contatos sem histórico acompanham a entidade.</p>
      <CampoBusca valor={busca} setValor={setBusca} rotulo="Cliente de destino" ajuda="Nome, código ou CNPJ" teste="busca-destino-transferencia" total={null} />
      <label className="campo">
        Cliente de destino
        <select value={destino} onChange={(x) => setDestino(x.target.value)} data-teste="destino-transferencia">
          <option value="">Selecione…</option>
          {(lista.dados ?? [])
            .filter((x) => x.id !== c.id)
            .map((x) => (
              <option key={x.id} value={x.id}>
                {x.codigo} · {x.nome}
              </option>
            ))}
        </select>
      </label>
      {erro && (
        <p className="aviso aviso-erro" role="alert" data-teste="erro-transferencia">
          {erro}
        </p>
      )}
      <label className="campo">
        Justificativa (obrigatória, registrada na auditoria e nas duas Timelines)
        <textarea rows={3} value={justificativa} onChange={(x) => setJustificativa(x.target.value)} maxLength={1000} data-teste="justificativa-transferencia" />
      </label>
    </Modal>
  );
}

function ModalStatusCliente({ cliente: c, aoFechar, aoSalvar }: { cliente: Cliente; aoFechar: () => void; aoSalvar: () => void }) {
  const inativar = c.status === 'ativo';
  const [justificativa, setJustificativa] = useState('');
  const [erro, setErro] = useState('');
  const confirmar = async () => {
    setErro('');
    try {
      await api.post(`/api/clientes/${c.id}/${inativar ? 'inativar' : 'reativar'}`, { justificativa });
      aoSalvar();
    } catch (x) {
      setErro((x as Error).message);
    }
  };
  return (
    <Modal
      titulo={`${inativar ? 'Inativar' : 'Reativar'} cliente — ${c.nome}`}
      aoFechar={aoFechar}
      rodape={
        <>
          <button type="button" className="botao-secundario" onClick={aoFechar}>
            Voltar
          </button>
          <button type="button" className="botao-primario" onClick={confirmar} disabled={!justificativa.trim()} data-teste="confirmar-status-cliente">
            {inativar ? 'Inativar' : 'Reativar'}
          </button>
        </>
      }
    >
      <p>{inativar ? 'O histórico, as entidades e os contatos são preservados. Um cliente inativo não pode ser editado até ser reativado.' : 'O cliente volta a poder ser editado e usado.'}</p>
      {erro && (
        <p className="aviso aviso-erro" role="alert">
          {erro}
        </p>
      )}
      <label className="campo">
        Justificativa (obrigatória, registrada na auditoria)
        <textarea rows={3} value={justificativa} onChange={(e) => setJustificativa(e.target.value)} maxLength={1000} data-teste="justificativa-status-cliente" />
      </label>
    </Modal>
  );
}
